/**
 * combat/emotes.ts — How You Say What Words Can't
 *
 * Wave. Dance. Laugh. Be surprised. React to the world not through text
 * but through your body — your character's body, moving in ways that
 * transcend the chat box.
 *
 * Emotes are character animations with visual feedback: a floating symbol,
 * a subtle bubble, a momentary particle burst. They're social currency —
 * more expressive than words, more nuanced than emoji.
 */

import type { GameState, FloatingEmote } from "../engine/state.js";

// ─── Emote Definition ─────────────────────────────────────────────────────

export interface EmoteDefinition {
  id: string;
  name: string;
  /** Symbol shown in the radial wheel and as floating feedback */
  symbol: string;
  /** Color tint for the floating symbol */
  color: string;
  /** Character animation state to trigger */
  animation: string;
  /** Animation duration in seconds */
  duration: number;
  /** Sound effect key */
  sound: string;
  /** Particle effect (null = no particles) */
  particles: string | null;
  /** Category for organization in the wheel */
  category: "social" | "reaction" | "dance" | "expression";
  /** Tooltip description */
  description: string;
}

// ═══════════════════════════════════════════════════════════════════════════
//  EMOTE DEFINITIONS — 16 expressive reactions
// ═══════════════════════════════════════════════════════════════════════════

export const EMOTES: EmoteDefinition[] = [
  // ── Social ─────────────────────────────────────────────────────────────
  {
    id: "wave",
    name: "Wave",
    symbol: "👋",
    color: "#FFDD66",
    animation: "emote_wave",
    duration: 1.8,
    sound: "emote_wave",
    particles: null,
    category: "social",
    description: "Friendly wave — say hello!",
  },
  {
    id: "bow",
    name: "Bow",
    symbol: "🙇",
    color: "#CCAA88",
    animation: "emote_bow",
    duration: 2.0,
    sound: "emote_bow",
    particles: null,
    category: "social",
    description: "A respectful bow",
  },
  {
    id: "salute",
    name: "Salute",
    symbol: "🫡",
    color: "#88AACC",
    animation: "emote_salute",
    duration: 1.5,
    sound: "emote_salute",
    particles: null,
    category: "social",
    description: "Sharp salute — respect",
  },
  {
    id: "clap",
    name: "Clap",
    symbol: "👏",
    color: "#FFCC44",
    animation: "emote_clap",
    duration: 2.2,
    sound: "emote_clap",
    particles: "sparkle_burst",
    category: "social",
    description: "Applause! Well done",
  },

  // ── Reactions ──────────────────────────────────────────────────────────
  {
    id: "surprised",
    name: "Surprised",
    symbol: "😮",
    color: "#FFAA44",
    animation: "emote_surprised",
    duration: 1.5,
    sound: "emote_gasp",
    particles: "exclamation",
    category: "reaction",
    description: "Jaw-dropping surprise",
  },
  {
    id: "shocked",
    name: "Shocked",
    symbol: "⚡",
    color: "#FFEE00",
    animation: "emote_shocked",
    duration: 1.8,
    sound: "emote_shock",
    particles: "lightning_sparks",
    category: "reaction",
    description: "Total shock — what just happened?!",
  },
  {
    id: "laugh",
    name: "Laugh",
    symbol: "😂",
    color: "#FFDD88",
    animation: "emote_laugh",
    duration: 2.5,
    sound: "emote_laugh",
    particles: null,
    category: "reaction",
    description: "Can't stop laughing",
  },
  {
    id: "cry",
    name: "Cry",
    symbol: "😢",
    color: "#88BBDD",
    animation: "emote_cry",
    duration: 2.5,
    sound: "emote_cry",
    particles: "tear_drops",
    category: "reaction",
    description: "Tears — sad or moved",
  },

  // ── Dance ──────────────────────────────────────────────────────────────
  {
    id: "dance",
    name: "Dance",
    symbol: "💃",
    color: "#FF66AA",
    animation: "emote_dance",
    duration: 4.0,
    sound: "emote_dance",
    particles: "music_notes",
    category: "dance",
    description: "Break it down!",
  },
  {
    id: "groove",
    name: "Groove",
    symbol: "🕺",
    color: "#AA66FF",
    animation: "emote_groove",
    duration: 4.0,
    sound: "emote_groove",
    particles: "music_notes",
    category: "dance",
    description: "Feel the rhythm",
  },
  {
    id: "spin",
    name: "Spin",
    symbol: "🌀",
    color: "#66CCFF",
    animation: "emote_spin",
    duration: 2.0,
    sound: "emote_spin",
    particles: "spiral_trail",
    category: "dance",
    description: "Dizzy spin!",
  },
  {
    id: "air_guitar",
    name: "Air Guitar",
    symbol: "🎸",
    color: "#FF4444",
    animation: "emote_air_guitar",
    duration: 3.0,
    sound: "emote_guitar",
    particles: "music_notes",
    category: "dance",
    description: "Shred an invisible guitar",
  },

  // ── Expression ─────────────────────────────────────────────────────────
  {
    id: "think",
    name: "Think",
    symbol: "🤔",
    color: "#CCAA66",
    animation: "emote_think",
    duration: 2.5,
    sound: "emote_hmm",
    particles: null,
    category: "expression",
    description: "Deep in thought...",
  },
  {
    id: "shrug",
    name: "Shrug",
    symbol: "🤷",
    color: "#AAAAAA",
    animation: "emote_shrug",
    duration: 1.5,
    sound: "emote_shrug",
    particles: null,
    category: "expression",
    description: "¯\\_(ツ)_/¯",
  },
  {
    id: "flex",
    name: "Flex",
    symbol: "💪",
    color: "#FF8844",
    animation: "emote_flex",
    duration: 2.0,
    sound: "emote_flex",
    particles: "sparkle_burst",
    category: "expression",
    description: "Show off those muscles",
  },
  {
    id: "sleep",
    name: "Sleep",
    symbol: "💤",
    color: "#8888CC",
    animation: "emote_sleep",
    duration: 5.0,
    sound: "emote_snore",
    particles: "zzz_float",
    category: "expression",
    description: "Catching some Z's",
  },
];

// ─── Lookup ───────────────────────────────────────────────────────────────

const EMOTE_MAP = new Map<string, EmoteDefinition>();
for (const e of EMOTES) EMOTE_MAP.set(e.id, e);

export function getEmote(id: string): EmoteDefinition | undefined {
  return EMOTE_MAP.get(id);
}

export function getEmotesByCategory(category: EmoteDefinition["category"]): EmoteDefinition[] {
  return EMOTES.filter(e => e.category === category);
}

// ─── Emote Execution ──────────────────────────────────────────────────────

/** Minimum time between emotes (seconds) */
const EMOTE_COOLDOWN = 1.5;

/**
 * Trigger an emote if off cooldown. Spawns the floating symbol,
 * sets the character animation state, and queues the sound effect.
 * Returns true if the emote fired.
 */
export function triggerEmote(state: GameState, emoteId: string): boolean {
  const emote = EMOTE_MAP.get(emoteId);
  if (!emote) return false;

  const now = performance.now() / 1000;
  const wheel = state.player.emoteWheel;

  // Cooldown check
  if (now - wheel.lastEmoteAt < EMOTE_COOLDOWN) return false;

  wheel.lastEmoteAt = now;
  wheel.open = false;

  // Spawn floating symbol at player position
  const px = state.player.position.tileX;
  const py = state.player.position.tileY;
  const screenX = (px - py) * 32;
  const screenY = (px + py) * 16 - 50; // above the character's head

  const floating: FloatingEmote = {
    symbol: emote.symbol,
    x: screenX,
    y: screenY,
    createdAt: now,
    color: emote.color,
  };

  state.player.floatingEmotes.push(floating);

  // Set character animation override
  // Map emote to the closest engine AnimState for the composer
  const animMap: Record<string, string> = {
    wave: "wave", bow: "emote", salute: "emote", clap: "emote",
    surprised: "emote", shocked: "emote", laugh: "emote", cry: "emote",
    dance: "dance", groove: "dance", spin: "dance", air_guitar: "dance",
    think: "emote", shrug: "emote", flex: "emote", sleep: "sleep",
  };
  const targetAnim = animMap[emoteId] ?? "emote";
  state.player.animState = targetAnim as any; // AnimState union — safe cast
  state.player.activeEmoteId = emoteId;
  state.player.activeEmoteUntil = now + emote.duration;

  // Queue sound
  state.ui.uiSounds.push(emote.sound);

  return true;
}

// ─── Floating Emote Cleanup ───────────────────────────────────────────────

const FLOATING_EMOTE_LIFETIME = 2.0; // seconds

/** Remove expired floating emotes */
export function cleanupFloatingEmotes(state: GameState): void {
  const now = performance.now() / 1000;
  state.player.floatingEmotes = state.player.floatingEmotes.filter(
    e => now - e.createdAt < FLOATING_EMOTE_LIFETIME,
  );
}

// ─── Emote Wheel Helpers ──────────────────────────────────────────────────

/** Open the emote wheel */
export function openEmoteWheel(state: GameState): void {
  const wheel = state.player.emoteWheel;
  wheel.open = true;
  wheel.openedAt = performance.now() / 1000;
  wheel.selectedIndex = -1;
}

/** Close the emote wheel without triggering an emote */
export function closeEmoteWheel(state: GameState): void {
  state.player.emoteWheel.open = false;
  state.player.emoteWheel.selectedIndex = -1;
}

/**
 * Update wheel selection based on mouse angle from center.
 * Returns the index of the emote slice the cursor is hovering.
 */
export function updateWheelSelection(
  mouseAngle: number,
  emoteCount: number,
): number {
  if (emoteCount === 0) return -1;

  // Normalize angle to [0, 2π), with 0 pointing up
  let a = mouseAngle + Math.PI / 2;
  if (a < 0) a += Math.PI * 2;
  if (a >= Math.PI * 2) a -= Math.PI * 2;

  const sliceAngle = (Math.PI * 2) / emoteCount;
  return Math.floor(a / sliceAngle) % emoteCount;
}
